import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  BookOpen,
  Clock,
  BarChart3,
  ChevronRight,
  ChevronDown,
  Sparkles
} from 'lucide-react';
import { useCategoryLibrary } from '../hooks/useCategoryLibrary';
import { CategoryItem, CategoryItemSectioned } from '../types/category';
import { CATEGORY_GROUPS } from '../data/categories';
import { getProgress, ChapterInfo } from '../utils/indexedDB';

// Icon mapping for categories
const iconMap: Record<string, React.FC<{ className?: string }>> = {
  Rocket: ({ className }) => <span className={className}>🚀</span>,
  Wand2: ({ className }) => <span className={className}>🪄</span>,
  Search: ({ className }) => <span className={className}>🔍</span>,
  Heart: ({ className }) => <span className={className}>❤️</span>,
  Skull: ({ className }) => <span className={className}>💀</span>,
  Compass: ({ className }) => <span className={className}>🧭</span>,
  BookOpen: ({ className }) => <span className={className}>📖</span>,
  Smile: ({ className }) => <span className={className}>😊</span>,
  Drama: ({ className }) => <span className={className}>🎭</span>,
  User: ({ className }) => <span className={className}>👤</span>,
  Flask: ({ className }) => <span className={className}>🧪</span>,
  Clock: ({ className }) => <span className={className}>🕐</span>,
  Cpu: ({ className }) => <span className={className}>💻</span>,
  Leaf: ({ className }) => <span className={className}>🍃</span>,
  HeartPulse: ({ className }) => <span className={className}>💗</span>,
  Briefcase: ({ className }) => <span className={className}>💼</span>,
  Brain: ({ className }) => <span className={className}>🧠</span>,
  MapPin: ({ className }) => <span className={className}>📍</span>,
  Palette: ({ className }) => <span className={className}>🎨</span>,
  Book: ({ className }) => <span className={className}>📚</span>,
  Shield: ({ className }) => <span className={className}>🛡️</span>,
  Trophy: ({ className }) => <span className={className}>🏆</span>,
  AlertTriangle: ({ className }) => <span className={className}>⚠️</span>,
  Sun: ({ className }) => <span className={className}>☀️</span>,
  Landmark: ({ className }) => <span className={className}>🏛️</span>,
};

interface ContentLibraryProps {
  onSelectItem: (item: CategoryItemSectioned) => void;
  onCategoryChange?: (categoryIds: string[]) => void;
  userTier?: 'guest' | 'starter' | 'reader' | 'ultra'; // For tier-based feature gating
}

export const ContentLibrary: React.FC<ContentLibraryProps> = ({ onSelectItem, onCategoryChange, userTier = 'guest' }) => {
    const {
      categories,
      items,
      selectedCategories,
      toggleCategory,
      searchQuery,
      setSearchQuery,
      getCategoryItemCount,
      isCategoryLoading,
      loading
    } = useCategoryLibrary();

  // State to store progress for each item
  const [itemProgress, setItemProgress] = useState<Record<string, any>>({});

  // Collapsed groups state — empty set means all expanded (default)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Group categories by their groupId
  const categoriesByGroup = useMemo(() => {
    const map: Record<string, typeof categories> = {};
    CATEGORY_GROUPS.forEach(group => {
      map[group.id] = categories.filter(c => c.groupId === group.id);
    });
    return map;
  }, [categories]);

  // Get total item count for a group using static category itemCount
  const getGroupItemCount = (groupId: string): number => {
    return categoriesByGroup[groupId]?.reduce((sum, cat) => sum + cat.itemCount, 0) || 0;
  };

  // Toggle group collapse
  const toggleGroupCollapse = (groupId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Ref for the stories section to enable scroll-to behavior
  const storiesSectionRef = useRef<HTMLDivElement>(null);

  // Emit category changes to parent
  useEffect(() => {
    onCategoryChange?.(selectedCategories);
  }, [selectedCategories, onCategoryChange]);

  // Scroll to stories section when a category is first selected
  useEffect(() => {
    if (selectedCategories.length > 0 && storiesSectionRef.current) {
      // Small delay to let the stories grid render before scrolling
      const timer = setTimeout(() => {
        storiesSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedCategories]);

  // Fetch progress for all items
  useEffect(() => {
    async function fetchProgress() {
      const progressMap: Record<string, any> = {};
      await Promise.all(
        items.map(async (item) => {
          const progress = await getProgress(item.id);
          if (progress) {
            progressMap[item.id] = progress;
          }
        })
      );
      setItemProgress(progressMap);
    }
    if (items.length > 0) {
      fetchProgress();
    }
  }, [items]);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20';
      case 'intermediate':
        return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20';
      case 'advanced':
        return 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20';
      default:
        return 'bg-slate-500/10 text-slate-700 dark:text-slate-400 border-slate-500/20';
    }
  };

  const getDifficultyDot = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-emerald-500';
      case 'intermediate':
        return 'bg-amber-500';
      case 'advanced':
        return 'bg-rose-500';
      default:
        return 'bg-slate-500';
    }
  };

  const getPreview = (content: string, maxLength: number = 120): string => {
    const cleaned = content.replace(/\s+/g, ' ').trim();
    if (cleaned.length <= maxLength) return cleaned;
    return cleaned.substring(0, maxLength) + '...';
  };

  // Calculate reading time at different WPM speeds
  const getReadingTimeAtWpm = (wordCount: number, wpm: number): string => {
    const totalSeconds = Math.round((wordCount / wpm) * 60);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes === 0) return `${seconds}s`;
    if (seconds === 0) return `${minutes}m`;
    return `${minutes}m ${seconds}s`;
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-blue-500" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
            Browse Categories
          </h2>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Choose a category, then choose a story. Scroll down to see more stories.
        </p>
      </div>

       {/* Modern Category Filter Chips — grouped */}
       <div className="mb-8 space-y-3">
         {/* Grouped filter chips */}
        {CATEGORY_GROUPS.map(group => {
          const groupCategories = categoriesByGroup[group.id] || [];
          if (groupCategories.length === 0) return null;

          return (
            <div key={group.id} className="flex flex-wrap gap-2 items-center">
              <span className="inline-flex items-center px-3 py-1.5 text-xs font-extrabold rounded-md bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 flex-shrink-0 uppercase tracking-wider">
                {group.name}
              </span>
              {groupCategories.map(category => {
                const IconComponent = iconMap[category.icon] || BookOpen;
                const isLoading = isCategoryLoading(category.id);
                const isSelected = selectedCategories.includes(category.id);

                return (
                  <button
                    key={category.id}
                    onClick={() => toggleCategory(category.id)}
                    className={`
                      group px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200
                      flex items-center gap-2 flex-shrink-0
                      ${isSelected
                        ? 'text-white shadow-md'
                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                      }
                    `}
                    style={{
                      backgroundColor: isSelected ? category.color : undefined,
                      borderColor: isSelected ? category.color : undefined
                    }}
                  >
                    <span className={`transition-transform duration-200 ${isSelected ? 'scale-110' : 'group-hover:scale-110'}`}>
                      <IconComponent className="h-4 w-4" />
                    </span>
                    <span>{category.name}</span>
                    {!isLoading && (
                      <span className={`
                        text-xs px-2 py-0.5 rounded transition-colors flex-shrink-0
                        ${isSelected
                          ? 'bg-white/25 text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                        }
                      `}>
                        {category.itemCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="relative h-12 w-12 mx-auto mb-4">
            <div className="absolute inset-0 border-2 border-slate-200 dark:border-slate-700 rounded-full"></div>
            <div className="absolute inset-0 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-slate-500 dark:text-slate-400 font-medium">Loading categories...</p>
        </div>
      )}

      {/* Category Items List */}
      {!loading && selectedCategories.length > 0 && (
        <div ref={storiesSectionRef} className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* Category Header - Show summary when multiple selected, or single category details */}
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200 dark:border-slate-700">
            {selectedCategories.length === 1 ? (() => {
              const category = categories.find(c => c.id === selectedCategories[0]);
              if (category) {
                const IconComponent = iconMap[category.icon] || BookOpen;
                return (
                  <>
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                      style={{
                        backgroundColor: `${category.color}15`,
                      }}
                    >
                      <span style={{ color: category.color }}>
                        <IconComponent className="h-6 w-6" />
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
                        {category.name}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {category.description}
                      </p>
                    </div>
                  </>
                );
              }
              return null;
            })() : (
              <>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl bg-slate-100 dark:bg-slate-700">
                  <span className="text-2xl">📚</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
                    {selectedCategories.length} Categories Selected
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Showing texts from {items.length} items across selected categories
                  </p>
                </div>
              </>
            )}
          </div>

          {items.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="h-8 w-8 text-slate-400 dark:text-slate-500" />
              </div>
              <p className="text-slate-600 dark:text-slate-400 font-medium">
                {searchQuery ? 'No items match your search' : 'No texts in this category yet'}
              </p>
              <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                {searchQuery ? 'Try a different search term' : 'Texts will appear here when added'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item, index) => (
                <div
                  key={item.id}
                  onClick={() => onSelectItem(item)}
                  className="
                    group relative p-4 bg-white dark:bg-slate-800
                    border border-slate-200 dark:border-slate-700
                    rounded-xl transition-all duration-300
                    hover:shadow-xl hover:shadow-slate-200/50 dark:hover:shadow-black/20
                    hover:border-blue-300 dark:hover:border-blue-600
                    hover:-translate-y-1
                    cursor-pointer
                    animate-in fade-in zoom-in-95 duration-300
                    flex flex-col
                  "
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  {/* Hover Gradient Overlay */}
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 dark:from-blue-500/10 dark:to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                  <div className="relative flex flex-col flex-1">
                    {/* Top: Badge + Word Count */}
                    <div className="flex items-center justify-between mb-3">
                      <span className={`
                        px-2 py-0.5 text-xs font-medium rounded-full border
                        ${getDifficultyColor(item.difficulty)}
                      `}>
                        <span className="flex items-center gap-1">
                          <span className={`w-1.5 h-1.5 rounded-full ${getDifficultyDot(item.difficulty)}`} />
                          {item.difficulty.charAt(0).toUpperCase() + item.difficulty.slice(1)}
                        </span>
                      </span>
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        {item.wordCount.toLocaleString()}
                        <span className="text-xs font-normal text-slate-400 ml-1">words</span>
                      </span>
                    </div>

                    {/* Category Badge */}
                    {(() => {
                      const itemCategory = categories.find(c => c.id === item.categoryId);
                      if (itemCategory) {
                        const CategoryIcon = iconMap[itemCategory.icon] || BookOpen;
                        return (
                          <div className="flex items-center gap-1.5 mb-2">
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full"
                              style={{
                                backgroundColor: `${itemCategory.color}15`,
                                color: itemCategory.color
                              }}
                            >
                              <CategoryIcon className="h-3 w-3" />
                              {itemCategory.name}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Title */}
                    <h3 className="font-semibold text-slate-900 dark:text-white text-base mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2">
                      {item.title}
                    </h3>

                    {/* Summary */}
                    <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-4 leading-relaxed mb-4 flex-1">
                      {(item as any).spoilerFreeSummary || getPreview(item.content, 100)}
                    </p>

                    {/* Source */}
                    {item.source && (
                      <p className="text-xs text-slate-400 dark:text-slate-500 mb-3 truncate">
                        {item.source}
                      </p>
                    )}

                    {/* Bottom: Reading Times */}
                    <div className="pt-3 border-t border-slate-100 dark:border-slate-700 mt-auto">
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-slate-400 dark:text-slate-500">@ 200</span>
                        <span className="font-medium text-slate-600 dark:text-slate-300">
                          {getReadingTimeAtWpm(item.wordCount, 200)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-400 dark:text-slate-500">@ 500</span>
                        <span className="font-medium text-slate-600 dark:text-slate-300">
                          {getReadingTimeAtWpm(item.wordCount, 500)}
                        </span>
                      </div>

                      {/* Chapter Progress (Reader+ tiers only) */}
                      {(userTier === 'reader' || userTier === 'ultra') && itemProgress[item.id] && (() => {
                        const progress = itemProgress[item.id];
                        const totalWords = progress.totalWords || item.wordCount;
                        const progressPercent = totalWords > 0 ? (progress.currentWordIndex / totalWords) * 100 : 0;
                        return (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-slate-400">Progress</span>
                              <span className="font-medium text-slate-600 dark:text-slate-300">
                                {Math.round(progressPercent)}%
                              </span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                                style={{ width: `${progressPercent}%` }}
                              />
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Category Grid View — grouped with collapsible sections */}
      {!loading && selectedCategories.length === 0 && (
        <div className="animate-in fade-in duration-500 space-y-8">
          {CATEGORY_GROUPS.map(group => {
            const groupCategories = categoriesByGroup[group.id] || [];
            if (groupCategories.length === 0) return null;

            const isCollapsed = collapsedGroups.has(group.id);
            const groupItemCount = getGroupItemCount(group.id);
            const categoryCount = groupCategories.length;

            // Adaptive grid: smaller groups get wider cards
            const gridClass = categoryCount <= 2
              ? 'grid grid-cols-1 sm:grid-cols-2 gap-4'
              : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4';

            return (
              <div key={group.id}>
                {/* Group Header — collapsible */}
                <button
                  onClick={() => toggleGroupCollapse(group.id)}
                  className="
                    w-full flex items-center gap-3 mb-4 pb-3
                    border-b-2 transition-colors duration-200
                    hover:opacity-80 text-left
                  "
                  style={{ borderColor: group.color }}
                >
                  <span className="text-2xl">{group.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                        {group.name}
                      </h3>
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        {categoryCount} {categoryCount === 1 ? 'category' : 'categories'} · {groupItemCount} texts
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {group.description}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-slate-400 dark:text-slate-500">
                    {isCollapsed ? (
                      <ChevronRight className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </div>
                </button>

                {/* Group Categories Grid */}
                {!isCollapsed && (
                  <div className={gridClass}>
                    {groupCategories.map((category, index) => {
                      const IconComponent = iconMap[category.icon] || BookOpen;
                      const itemCount = getCategoryItemCount(category.id);
                      const isLoading = isCategoryLoading(category.id);

                      return (
                        <button
                          key={category.id}
                          onClick={() => toggleCategory(category.id)}
                          className="
                            group relative text-left p-5
                            bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-900
                            border border-slate-200 dark:border-slate-600
                            shadow-sm shadow-slate-200/50 dark:shadow-black/20
                            rounded-xl transition-all duration-300
                            hover:shadow-xl hover:shadow-slate-300/60 dark:hover:shadow-black/40
                            hover:border-slate-300 dark:hover:border-slate-500
                            hover:-translate-y-1
                            overflow-hidden
                            animate-in fade-in zoom-in-95 duration-300
                          "
                          style={{
                            animationDelay: `${index * 30}ms`,
                          }}
                        >
                          {/* Top Accent Bar */}
                          <div
                            className="absolute left-0 right-0 top-0 h-1 transition-all duration-300"
                            style={{ backgroundColor: category.color }}
                          />

                          {/* Left Accent Border */}
                          <div
                            className="absolute left-0 top-0 bottom-0 w-1 transition-all duration-300 group-hover:w-1.5"
                            style={{ backgroundColor: category.color }}
                          />

                          {/* Background Gradient on Hover */}
                          <div
                            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                            style={{
                              background: `linear-gradient(135deg, ${category.color}10 0%, transparent 50%)`
                            }}
                          />

                          <div className="relative pt-2">
                            {/* Icon */}
                            <div
                              className="w-11 h-11 rounded-lg flex items-center justify-center text-xl mb-3 transition-all duration-300 group-hover:scale-110 group-hover:shadow-md"
                              style={{
                                backgroundColor: `${category.color}20`,
                                boxShadow: `0 2px 8px ${category.color}20`
                              }}
                            >
                              <span style={{ color: category.color }}>
                                <IconComponent className="h-5 w-5" />
                              </span>
                            </div>

                            {/* Title */}
                            <h4 className="font-semibold text-slate-900 dark:text-white mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                              {category.name}
                            </h4>

                            {/* Description */}
                            <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mb-3 leading-relaxed">
                              {category.description}
                            </p>

                            {/* Count */}
                            <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700/50">
                              <span
                                className="text-xs font-medium px-2 py-1 rounded-full"
                                style={{
                                  backgroundColor: `${category.color}15`,
                                  color: category.color
                                }}
                              >
                                {isLoading ? (
                                  <span className="flex items-center gap-1">
                                    <span className="w-1 h-1 rounded-full animate-bounce" style={{ backgroundColor: category.color, animationDelay: '0ms' }} />
                                    <span className="w-1 h-1 rounded-full animate-bounce" style={{ backgroundColor: category.color, animationDelay: '150ms' }} />
                                    <span className="w-1 h-1 rounded-full animate-bounce" style={{ backgroundColor: category.color, animationDelay: '300ms' }} />
                                  </span>
                                ) : (
                                  `${category.itemCount} ${category.itemCount === 1 ? 'text' : 'texts'}`
                                )}
                              </span>

                              {/* Arrow indicator */}
                              <ChevronRight
                                className="h-4 w-4 text-slate-300 dark:text-slate-600 transition-all duration-300 group-hover:text-blue-500 group-hover:translate-x-0.5"
                              />
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContentLibrary;
